import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskApi, usersApi, patientApi, formsApi } from '../../lib/api';
import api from '../../lib/api';
import { createTaskResilient, updateTaskResilient } from '../../lib/offlineQueue';
import OfflineQueuePanel from '../../components/OfflineQueuePanel';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Loader2, CheckCircle, Clock, AlertTriangle,
  User, ArrowRight, LayoutGrid, Table2, Trash2, FileText, Edit3
} from 'lucide-react';

const STATUS_CFG = {
  pending: { color: '#CA8A04', icon: Clock, label: 'Pending', bg: '#FEF9C3' },
  in_progress: { color: '#1A56DB', icon: ArrowRight, label: 'In Progress', bg: '#DBEAFE' },
  completed: { color: '#166534', icon: CheckCircle, label: 'Completed', bg: '#DCFCE7' },
};
const PRIORITY_CFG = {
  low: { color: '#64748B', label: 'Low' },
  medium: { color: '#CA8A04', label: 'Medium' },
  high: { color: '#DC2626', label: 'High' },
};

export default function TasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canAssign = ['super_admin', 'practice_admin'].includes(user?.role);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [viewMode, setViewMode] = useState('kanban'); // kanban | table
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newAssignee, setNewAssignee] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newPatientId, setNewPatientId] = useState('');
  const [newFormId, setNewFormId] = useState('');
  const [patients, setPatients] = useState([]);
  const [forms, setForms] = useState([]);
  const [saving, setSaving] = useState(false);
  // edit-mode state — reuses the same create dialog for both flows
  const [editingTaskId, setEditingTaskId] = useState(null);

  const loadPatientsAndForms = async () => {
    try {
      const [pRes, fRes] = await Promise.all([
        patientApi.list({ limit: 500 }).catch(() => ({ data: [] })),
        formsApi.list().catch(() => ({ data: [] })),
      ]);
      const pList = Array.isArray(pRes.data) ? pRes.data : (pRes.data.patients || []);
      setPatients(pList);
      const fList = Array.isArray(fRes.data) ? fRes.data : (fRes.data.forms || []);
      setForms(fList);
    } catch (err) { console.error('TasksPage: loadPatientsAndForms failed', err); }
  };

  const loadUsers = async () => {
    if (!canAssign) return;
    try {
      const res = await usersApi.list();
      setUsers(res.data.users || []);
    } catch (err) { console.error('TasksPage: loadUsers failed', err); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await taskApi.list(params);
      setTasks(res.data.tasks || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { loadUsers(); loadPatientsAndForms(); }, []);

  const handleCreate = async () => {
    if (!newTitle.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: newTitle, description: newDesc, priority: newPriority,
        assigned_to: newAssignee || null, due_date: newDueDate || null,
      };
      if (newPatientId) payload.patient_id = newPatientId;
      if (newFormId) {
        payload.form_id = newFormId;
        const f = forms.find(x => x.id === newFormId);
        if (f?.name) payload.form_name = f.name;
      }
      if (editingTaskId) {
        // PATCH-style: only push fields that the backend accepts in PUT.
        await taskApi.update(editingTaskId, {
          title: newTitle, description: newDesc, priority: newPriority,
          assigned_to: newAssignee || null, due_date: newDueDate || null,
        });
        toast.success('Task updated');
      } else {
        const res = await createTaskResilient(api, payload);
        if (!res?.data?.offline) toast.success('Task created');
      }
      setShowCreate(false);
      setEditingTaskId(null);
      setNewTitle(''); setNewDesc(''); setNewPriority('medium');
      setNewAssignee(''); setNewDueDate('');
      setNewPatientId(''); setNewFormId('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const startEdit = (task) => {
    setEditingTaskId(task.id);
    setNewTitle(task.title || '');
    setNewDesc(task.description || '');
    setNewPriority(task.priority || 'medium');
    setNewAssignee(task.assigned_to || '');
    setNewDueDate(task.due_date ? String(task.due_date).slice(0, 10) : '');
    setNewPatientId(task.patient_id || '');
    setNewFormId(task.form_id || '');
    setShowCreate(true);
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await taskApi.delete(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete task');
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const res = await updateTaskResilient(api, taskId, updates);
      // Optimistically reflect change in UI regardless of online/offline
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
      if (res?.data?.offline) return; // silent — toast already shown by helper
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleStatusChange = (taskId, v) => updateTask(taskId, { status: v });
  const handlePriorityChange = (taskId, v) => updateTask(taskId, { priority: v });
  const handleAssigneeChange = (taskId, v) => updateTask(taskId, { assigned_to: v === 'unassigned' ? null : v });

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const visibleTasks = assigneeFilter ? tasks.filter(t => (assigneeFilter === 'unassigned' ? !t.assigned_to : t.assigned_to === assigneeFilter)) : tasks;

  const onDrop = (e, newStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('taskId');
    if (id) handleStatusChange(id, newStatus);
  };

  const renderTaskCard = (task, draggable = false) => {
    const pCfg = PRIORITY_CFG[task.priority] || PRIORITY_CFG.medium;
    const canDelete = task.created_by === user?.id || ['super_admin', 'practice_admin'].includes(user?.role);
    const canEdit = task.created_by === user?.id || ['super_admin', 'practice_admin'].includes(user?.role);
    const hasForm = !!task.form_id;
    const statusBorder = {
      completed: 'border-l-[#166534]',
      in_progress: 'border-l-[#0E7490]',
      pending: 'border-l-transparent',
    }[task.status] || 'border-l-transparent';
    const statusBadge = {
      completed: { label: 'Completed', bg: '#166534' },
      in_progress: { label: 'In Progress', bg: '#0E7490' },
    }[task.status];

    const handleCardClick = () => {
      if (hasForm) {
        // Navigate to form with patient context + taskId so status auto-updates
        const qs = new URLSearchParams();
        if (task.patient_id) qs.set('patientId', task.patient_id);
        qs.set('taskId', task.id);
        navigate(`/forms/${task.form_id}/fill?${qs.toString()}`);
      } else if (task.patient_id) {
        navigate(`/patients/${task.patient_id}`);
      }
    };

    return (
      <div
        key={task.id}
        draggable={draggable}
        onDragStart={e => e.dataTransfer.setData('taskId', task.id)}
        onClick={hasForm || task.patient_id ? handleCardClick : undefined}
        className={`p-3 bg-white rounded-lg border border-[#E2E8F0] border-l-4 ${statusBorder} hover:shadow-md hover:border-[#0E7490] transition-all mb-2 ${hasForm || task.patient_id ? 'cursor-pointer' : 'cursor-grab'}`}
        data-testid={`task-${task.id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-[#0A2342] text-sm flex-1">{task.title}</h3>
          <div className="flex items-center gap-1">
            {statusBadge && (
              <span
                className="text-[9px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-white"
                style={{ backgroundColor: statusBadge.bg }}
                data-testid={`task-status-${task.id}`}
              >
                {statusBadge.label}
              </span>
            )}
            <span className="text-[9px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ backgroundColor: pCfg.color + '15', color: pCfg.color }}>{pCfg.label}</span>
            {canEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); startEdit(task); setShowCreate(true); }}
                className="p-0.5 rounded hover:bg-[#DBEAFE] text-[#1A56DB]"
                title="Edit task"
                data-testid={`task-edit-${task.id}`}
              >
                <Edit3 className="w-3 h-3" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }}
                className="p-0.5 rounded hover:bg-[#FEE2E2] text-[#991B1B]"
                title="Delete task"
                data-testid={`task-delete-${task.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        {task.description && <p className="text-xs text-[#64748B] mb-2 line-clamp-2">{task.description}</p>}
        <div className="flex items-center gap-2 text-[10px] text-[#94A3B8] flex-wrap">
          {task.patient_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.patient_name}</span>}
          {task.form_name && <span className="px-1.5 py-0.5 bg-[#0E7490]/10 text-[#0E7490] rounded flex items-center gap-1"><FileText className="w-2.5 h-2.5" />{task.form_name}</span>}
          {task.due_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(task.due_date).toLocaleDateString()}</span>}
          {hasForm && <span className="ml-auto text-[#0E7490] font-medium flex items-center gap-0.5">Open <ArrowRight className="w-3 h-3" /></span>}
        </div>
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {canAssign ? (
            <Select value={task.assigned_to || 'unassigned'} onValueChange={(v) => handleAssigneeChange(task.id, v)}>
              <SelectTrigger className="h-6 text-[10px] flex-1" data-testid={`task-assignee-${task.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name} ({u.role})</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            task.assigned_to && <span className="text-[10px] text-[#64748B]">→ {userMap[task.assigned_to] || 'User'}</span>
          )}
          <Select value={task.priority} onValueChange={(v) => handlePriorityChange(task.id, v)}>
            <SelectTrigger className="h-6 text-[10px] w-20"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low" className="text-xs">Low</SelectItem>
              <SelectItem value="medium" className="text-xs">Medium</SelectItem>
              <SelectItem value="high" className="text-xs">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <OfflineQueuePanel title="Pending Sync" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Tasks</h1>
          <p className="text-[#64748B] mt-1">{visibleTasks.length} task(s) • {canAssign ? 'Assign tasks to team members' : 'Your assigned tasks'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex bg-[#F1F5F9] rounded-lg p-0.5" data-testid="view-toggle">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition ${viewMode === 'kanban' ? 'bg-white text-[#0E7490] shadow-sm' : 'text-[#64748B]'}`} data-testid="view-kanban"><LayoutGrid className="w-3.5 h-3.5" />Kanban</button>
            <button onClick={() => setViewMode('table')} className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition ${viewMode === 'table' ? 'bg-white text-[#0E7490] shadow-sm' : 'text-[#64748B]'}`} data-testid="view-table"><Table2 className="w-3.5 h-3.5" />Table</button>
          </div>
          {canAssign && users.length > 0 && (
            <Select value={assigneeFilter || 'all'} onValueChange={(v) => setAssigneeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-44" data-testid="assignee-filter"><SelectValue placeholder="All Assignees" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="task-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button className="careiq-btn-primary" onClick={() => setShowCreate(true)} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" />New Task
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
      ) : viewMode === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tasks-kanban">
          {Object.entries(STATUS_CFG).map(([status, cfg]) => {
            const col = visibleTasks.filter(t => t.status === status);
            const SIcon = cfg.icon;
            return (
              <div
                key={status}
                onDragOver={e => e.preventDefault()}
                onDrop={e => onDrop(e, status)}
                className="min-h-[400px]"
                data-testid={`column-${status}`}
              >
                <div className="rounded-t-lg px-3 py-2 flex items-center justify-between" style={{ backgroundColor: cfg.bg }}>
                  <div className="flex items-center gap-2"><SIcon className="w-4 h-4" style={{ color: cfg.color }} /><span className="text-sm font-semibold" style={{ color: cfg.color }}>{cfg.label}</span></div>
                  <span className="text-xs px-2 py-0.5 bg-white rounded-full font-medium" style={{ color: cfg.color }}>{col.length}</span>
                </div>
                <div className="p-2 bg-[#F8FAFC] min-h-[360px] rounded-b-lg border border-t-0 border-[#E2E8F0]">
                  {col.length > 0 ? col.map(t => renderTaskCard(t, true)) : <div className="text-center text-xs text-[#94A3B8] py-12">Drag tasks here</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : visibleTasks.length > 0 ? (
        <Card className="careiq-card" data-testid="tasks-table">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F8FAFC] border-b">
                <tr><th className="text-left text-xs font-medium text-[#64748B] px-3 py-2">Title</th><th className="text-left text-xs font-medium text-[#64748B] px-3 py-2">Status</th><th className="text-left text-xs font-medium text-[#64748B] px-3 py-2">Priority</th><th className="text-left text-xs font-medium text-[#64748B] px-3 py-2">Assignee</th><th className="text-left text-xs font-medium text-[#64748B] px-3 py-2">Patient</th><th className="text-left text-xs font-medium text-[#64748B] px-3 py-2">Due</th></tr>
              </thead>
              <tbody>
                {visibleTasks.map(t => (
                  <tr key={t.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`task-row-${t.id}`}>
                    <td className="px-3 py-2 text-sm text-[#0A2342] max-w-xs">
                      <div className="font-medium truncate">{t.title}</div>
                      {t.description && <div className="text-xs text-[#64748B] truncate">{t.description}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <Select value={t.status} onValueChange={(v) => handleStatusChange(t.id, v)}>
                        <SelectTrigger className="h-7 w-28 text-[10px]"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="pending" className="text-xs">Pending</SelectItem><SelectItem value="in_progress" className="text-xs">In Progress</SelectItem><SelectItem value="completed" className="text-xs">Completed</SelectItem></SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Select value={t.priority} onValueChange={(v) => handlePriorityChange(t.id, v)}>
                        <SelectTrigger className="h-7 w-20 text-[10px]"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="low" className="text-xs">Low</SelectItem><SelectItem value="medium" className="text-xs">Medium</SelectItem><SelectItem value="high" className="text-xs">High</SelectItem></SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {canAssign ? (
                        <Select value={t.assigned_to || 'unassigned'} onValueChange={(v) => handleAssigneeChange(t.id, v)}>
                          <SelectTrigger className="h-7 w-36 text-[10px]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {users.map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (userMap[t.assigned_to] || <span className="text-[#94A3B8]">Unassigned</span>)}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#64748B]">{t.patient_name || '—'}</td>
                    <td className="px-3 py-2 text-xs text-[#64748B]">{t.due_date ? new Date(t.due_date).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : (
        <Card className="careiq-card"><CardContent className="py-16 text-center text-[#64748B]">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
          <p className="font-medium">No tasks</p>
          <p className="text-sm mt-1">Tasks are auto-created when forms are submitted</p>
        </CardContent></Card>
      )}

      <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) setEditingTaskId(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="create-task-dialog">
          <DialogHeader><DialogTitle>{editingTaskId ? 'Edit Task' : 'Create Task'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title *</Label><Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Task title" data-testid="task-title-input" /></div>
            <div><Label>Description</Label><Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional description" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Priority</Label>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger data-testid="task-priority-input"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Due Date</Label><Input type="date" value={newDueDate} onChange={e => setNewDueDate(e.target.value)} data-testid="task-due-input" /></div>
            </div>
            {canAssign && (
              <div><Label>Assign To</Label>
                <Select value={newAssignee || 'unassigned'} onValueChange={(v) => setNewAssignee(v === 'unassigned' ? '' : v)}>
                  <SelectTrigger data-testid="task-assign-input"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Patient (optional)</Label>
              <Select value={newPatientId || 'none'} onValueChange={(v) => setNewPatientId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="task-patient-input"><SelectValue placeholder="No patient" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">— None —</SelectItem>
                  {patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.mrn ? ` · ${p.mrn}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Form to Complete (optional)</Label>
              <Select value={newFormId || 'none'} onValueChange={(v) => setNewFormId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="task-form-input"><SelectValue placeholder="No form" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">— None —</SelectItem>
                  {forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowCreate(false); setEditingTaskId(null); }}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleCreate} disabled={saving} data-testid="save-task-btn">{saving ? (editingTaskId ? 'Saving…' : 'Creating…') : (editingTaskId ? 'Save Changes' : 'Create Task')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
